import React, { useContext } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import OperationContext from './context';
import { OperationContextType } from '../../../services/types/Operation';
import ToolkitsProvider from './ToolkitList/ToolkitsProvider';
import ToolkitList from './ToolkitList';
import ToolkitWrapper from './Toolkit';
import FinancialForm from './Toolkit/Espf/FinancialForm';
import EspfToolkitList from './ToolkitList/Espf';
import ToolkitPageSelector from './Toolkit/ToolkitPageSelector';
import MainContext from '../../App/MainProvider/context';
import Role from '../../../types/role';
import SupervisionReportProvider from './Flows/SupervisionReport/SupervisionReportProvider';
import SupervisionReport from './Flows/SupervisionReport';
import OperationHome from '.';
import Members from './Members';
import SupervisionReportEspfProvider from './Flows/SupervisionReportEspf/SupervisionReportEspfProvider';
import SupervisionReportEspf from './Flows/SupervisionReportEspf';
import InitialEsrsProvider from './Flows/InitialEsrs/InitialEsrsProvider';
import EssProvider from './Flows/Ess/EssProvider';
import EssCommentForm from './Flows/Ess/Comments/EssCommentForm';
import EssComments from './Flows/Ess/Comments';
import InitialEsrsCommentForm from './Flows/InitialEsrs/Comments/InitialEsrsCommentForm';
import InitialEsrsComments from './Flows/InitialEsrs/Comments';
import InitialEsrs from './Flows/InitialEsrs';
import Ess from './Flows/Ess';
import EsmrProvider from './Flows/Esmr/EsmrProvider';
import EsmrCommentForm from './Flows/Esmr/Comments/EsmrCommentForm';
import EsmrComments from './Flows/Esmr/Comments';
import Esmr from './Flows/Esmr';
import EsrsProvider from './Flows/Esrs/EsrsProvider';
import EsrsCommentForm from './Flows/Esrs/Comments/EsrsCommentForm';
import Esrs from './Flows/Esrs';
import EsrsComments from './Flows/Esrs/Comments';
import EsrrProvider from './Esrr/EsrrProvider';
import EsrrQuestionForm from './Esrr/Questions';
import Esrr from './Esrr';
import IndicatorProvider from './Indicator/IndicatorProvider';
import Indicator from './Indicator';
import CalculatorProvider from './Calculator/CalculatorProvider';
import Calculator from './Calculator';
import CalculatorSectorProvider from './Calculator/Sectors/UrbanInfrastructureTool/CalculatorSectorProvider';
import CalculatorSector from './Calculator/Sectors/UrbanInfrastructureTool';
import ActionPlan from './ActionPlan';
import ActionPlanItem from './ActionPlan/ActionPlanItem';
import ActionPlanProvider from './ActionPlan/ActionPlanProvider';
import ActionPlanItemProvider from './ActionPlan/ActionPlanItem/ActionPlanItemProvider';
import { SectorType } from './Calculator/Utils/sectorType';
import TransportRoadsCalculatorProvider from './Calculator/Sectors/TransportAndRoadRehabilitationTool/TransportRoadsCalculatorProvider';
import TransportRoadsCalculator from './Calculator/Sectors/TransportAndRoadRehabilitationTool';

const useIsViewer = (): boolean => {
  const { user } = useContext(MainContext);
  return user.roleCode === Role.Viewer;
};

const IfViewerRedirectToOperation: React.FC<{
  number: string;
  children?: React.ReactNode;
}> = ({ number, children }) => {
  const isViewer = useIsViewer();
  if (isViewer) {
    return <Navigate to={`/operations/${number}/`} replace />;
  }
  return children;
};

const ToolkitProviderWrapper = (operationId: string) => (
  <ToolkitsProvider id={operationId}>
    <Routes>
      <Route path="list" element={<ToolkitList />} />
      <Route path="form/:id?" element={<ToolkitWrapper type="classic" />} />
      <Route path="espf/form/fi/:id?" element={<ToolkitWrapper type="fi" />} />
      <Route path="espf/form/tcpo/:id?" element={<ToolkitWrapper type="tcpo" />} />
      <Route path="espf/form/other/:id?" element={<ToolkitWrapper type="other" />} />
      <Route path="espf/financial-form" element={<FinancialForm />} />
      <Route path="espf/list" element={<EspfToolkitList />} />
      <Route path="/" element={<ToolkitPageSelector />} />
    </Routes>
  </ToolkitsProvider>
);

const EssProviderWrapper = (operationId: string) => (
  <EssProvider id={operationId}>
    <Routes>
      <Route path="comments/form" element={<EssCommentForm />} />
      <Route path="comments" element={<EssComments />} />
      <Route path="/" element={<Ess />} />
    </Routes>
  </EssProvider>
);

const InitialEsrsProviderWrapper = (operationId: string) => (
  <InitialEsrsProvider id={operationId}>
    <Routes>
      <Route path="comments/form" element={<InitialEsrsCommentForm />} />
      <Route path="comments" element={<InitialEsrsComments />} />
      <Route path="/" element={<InitialEsrs />} />
    </Routes>
  </InitialEsrsProvider>
);

const SupervisionReportProviderWrapper = (operationId: string, operationNumber: string) => {
  const commonPath = `/operations/${operationNumber}/supervision-report`;
  return (
    <SupervisionReportProvider id={operationId}>
      <Routes>
        <Route path="/" element={<SupervisionReport />} />
        <Route path="*" element={<Navigate to={commonPath} replace />} />
      </Routes>
    </SupervisionReportProvider>
  );
};

const SupervisionReportEspfProviderWrapper = (operationId: string, operationNumber: string) => {
  const commonPath = `/operations/${operationNumber}/supervision-report-espf`;
  return (
    <SupervisionReportEspfProvider id={operationId}>
      <Routes>
        <Route path="/" element={<SupervisionReportEspf />} />
        <Route path="*" element={<Navigate to={commonPath} replace />} />
      </Routes>
    </SupervisionReportEspfProvider>
  );
};

const EsmrProviderWrapper = (operationId: string) => (
  <EsmrProvider id={operationId}>
    <Routes>
      <Route path="comments/form" element={<EsmrCommentForm />} />
      <Route path="comments" element={<EsmrComments />} />
      <Route path="/" element={<Esmr />} />
    </Routes>
  </EsmrProvider>
);

const EsrsProviderWrapper = (operationId: string) => (
  <EsrsProvider id={operationId}>
    <Routes>
      <Route path="comments/form" element={<EsrsCommentForm />} />
      <Route path="comments" element={<EsrsComments />} />
      <Route path="/" element={<Esrs />} />
    </Routes>
  </EsrsProvider>
);

const ActionPlanItemProviderWrapper = () => {
  const { actionPlanId } = useParams() as { operationId: string; actionPlanId: string };

  return (
    <ActionPlanItemProvider id={actionPlanId as string}>
      <ActionPlanItem />
    </ActionPlanItemProvider>
  );
};

const ActionPlanProviderWrapper = (operationId: string) => (
  <ActionPlanProvider id={operationId}>
    <Routes>
      <Route path=":actionPlanId" element={<ActionPlanItemProviderWrapper />} />
      <Route path="/" element={<ActionPlan />} />
    </Routes>
  </ActionPlanProvider>
);

const EsrrProviderWrapper = () => (
  <EsrrProvider>
    <Routes>
      <Route path="questions/:id?" element={<EsrrQuestionForm />} />
      <Route path="/" element={<Esrr />} />
    </Routes>
  </EsrrProvider>
);

const IndicatorProviderWrapper = () => (
  <IndicatorProvider>
    <Routes>
      <Route path="/" element={<Indicator />} />
    </Routes>
  </IndicatorProvider>
);

const CalculatorSectorProviderWrapper = ({ operationNumber }: { operationNumber: string }) => {
  const { calculateId } = useParams() as { operationId: string; calculateId: string };
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const sectorIdParam = query.get('sectorId');
  const sectorType = sectorIdParam ? (parseInt(sectorIdParam, 10) as SectorType) : null;

  // En caso de no encontrar calculateId o sectorType.
  if (!calculateId || sectorType === null || !Object.values(SectorType).includes(sectorType)) {
    // eslint-disable-next-line no-console
    console.warn('Invalid calculateId or missing/invalid sectorId in URL. Redirecting.');
    return <Navigate to={`/operations/${operationNumber}/calculator`} replace />;
  }

  switch (sectorType) {
    case SectorType.UrbanInfrastructureTool:
      return (
        <CalculatorSectorProvider id={calculateId}>
          <CalculatorSector />
        </CalculatorSectorProvider>
      );
    case SectorType.TransportAndRoadRehabilitationTool:
      return (
        <TransportRoadsCalculatorProvider id={calculateId}>
          <TransportRoadsCalculator />
        </TransportRoadsCalculatorProvider>
      );
    default:
      // eslint-disable-next-line no-console
      console.warn(`Unrecognized SectorType: ${sectorType}. Redirecting.`);
      return <Navigate to={`/operations/${operationNumber}/calculator`} replace />;
  }
};

const CalculatorProviderWrapper = (operationId: string, operationNumber: string) => (
  <CalculatorProvider id={operationId}>
    <Routes>
      <Route
        path=":calculateId"
        element={<CalculatorSectorProviderWrapper operationNumber={operationNumber} />}
      />
      <Route path="/" element={<Calculator />} />
    </Routes>
  </CalculatorProvider>
);

export const withOperationContext = <P extends object>(
  Component: React.ComponentType<P & { operationContext: OperationContextType }>
) => {
  const WithOperationContext = (props: P) => (
    <OperationContext.Consumer>
      {(context) => <Component {...props} operationContext={context} />}
    </OperationContext.Consumer>
  );
  return WithOperationContext;
};

const OperationRoutes = ({ operationContext }: { operationContext: OperationContextType }) => {
  if (operationContext.loading) {
    return <div>Loading...</div>;
  }

  const { operation } = operationContext;

  if (!operation) {
    return <div>No operation found</div>;
  }

  const { number } = operation;
  const id = operation.id.toString();

  return (
    <Routes>
      <Route path="toolkit/*" element={ToolkitProviderWrapper(id)} />
      <Route
        path="supervision-report/*"
        element={
          <IfViewerRedirectToOperation number={number}>
            {SupervisionReportProviderWrapper(id, number)}
          </IfViewerRedirectToOperation>
        }
      />
      <Route
        path="supervision-report-espf/*"
        element={
          <IfViewerRedirectToOperation number={number}>
            {SupervisionReportEspfProviderWrapper(id, number)}
          </IfViewerRedirectToOperation>
        }
      />

      <Route
        path="ess/*"
        element={
          <IfViewerRedirectToOperation number={number}>
            {EssProviderWrapper(id)}
          </IfViewerRedirectToOperation>
        }
      />

      <Route
        path="initial-esrs/*"
        element={
          <IfViewerRedirectToOperation number={number}>
            {InitialEsrsProviderWrapper(id)}
          </IfViewerRedirectToOperation>
        }
      />
      <Route
        path="esmr/*"
        element={
          <IfViewerRedirectToOperation number={number}>
            {EsmrProviderWrapper(id)}
          </IfViewerRedirectToOperation>
        }
      />
      <Route
        path="esrs/*"
        element={
          <IfViewerRedirectToOperation number={number}>
            {EsrsProviderWrapper(id)}
          </IfViewerRedirectToOperation>
        }
      />
      <Route path="esrr/*" element={EsrrProviderWrapper()} />
      <Route path="indicator/*" element={IndicatorProviderWrapper()} />
      <Route path="calculator/*" element={CalculatorProviderWrapper(id, number)} />
      <Route
        path="action-plan/*"
        element={
          <IfViewerRedirectToOperation number={number}>
            {ActionPlanProviderWrapper(id)}
          </IfViewerRedirectToOperation>
        }
      />
      <Route
        path="members/*"
        element={
          <IfViewerRedirectToOperation number={number}>
            <Members />
          </IfViewerRedirectToOperation>
        }
      />
      <Route path="*" element={<OperationHome />} />
    </Routes>
  );
};

// Envolver el componente con el HOC
export default withOperationContext(OperationRoutes);
